import { useState, useCallback } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Send,
  KeyRound,
  Users,
  Shield,
} from 'lucide-react';
import { UserRole, UserStatus } from '@barrikadave/shared';
import { STRINGS } from './constants';
import { getMappingStats } from './utils';
import type { MappedUser, RowValidation, ImportOptions, ImportProgress } from './types';
import { usersApi } from '../../services/api';

interface StepImportProps {
  mappedUsers: MappedUser[];
  validations: RowValidation[];
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
}

const CHUNK_SIZE = 50;

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
  STAF: 'Staf',
  USER: 'Perdorues',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Aktiv',
  PENDING: 'Ne pritje',
};

export default function StepImport({
  mappedUsers,
  validations,
  onBack,
  onClose,
  onSuccess,
}: StepImportProps) {
  const stats = getMappingStats(validations);
  const validUsers = mappedUsers.filter(
    (_, i) => validations[i]?.errors.length === 0,
  );

  const [options, setOptions] = useState<ImportOptions>({
    sendWelcomeEmail: false,
    autoGeneratePasswords: true,
    defaultRole: UserRole.USER,
    defaultStatus: UserStatus.ACTIVE,
  });

  const [phase, setPhase] = useState<'options' | 'progress' | 'results'>('options');
  const [progress, setProgress] = useState<ImportProgress>({
    total: validUsers.length,
    processed: 0,
    succeeded: 0,
    failed: 0,
    results: [],
  });

  const startImport = useCallback(async () => {
    setPhase('progress');

    const allResults: ImportProgress = {
      total: validUsers.length,
      processed: 0,
      succeeded: 0,
      failed: 0,
      results: [],
    };

    for (let i = 0; i < validUsers.length; i += CHUNK_SIZE) {
      const chunk = validUsers.slice(i, i + CHUNK_SIZE);
      const payload = chunk.map((u) => ({
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        password: u.password,
        role: u.role ? (u.role.toUpperCase() as UserRole) : undefined,
        phone: u.phone,
        position: u.position,
      }));

      try {
        const result = await usersApi.importBatch(payload, options);
        allResults.processed += chunk.length;
        allResults.succeeded += result.succeeded;
        allResults.failed += result.failed;
        allResults.results = [...allResults.results, ...result.results];
      } catch {
        allResults.processed += chunk.length;
        allResults.failed += chunk.length;
        allResults.results = [
          ...allResults.results,
          ...chunk.map((u) => ({
            email: u.email,
            success: false,
            error: 'Kerkesa deshtoi',
          })),
        ];
      }

      setProgress({ ...allResults });
    }

    setPhase('results');
    if (allResults.succeeded > 0) {
      onSuccess();
    }
  }, [validUsers, options, onSuccess]);

  if (phase === 'progress') {
    const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;
    return (
      <div className="space-y-6 py-4">
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700 mb-4">
            {STRINGS.importing} {progress.processed}/{progress.total} perdorues
          </p>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-purple-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {progress.succeeded} {STRINGS.successCount}, {progress.failed} {STRINGS.failedCount}
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    const failedResults = progress.results.filter((r) => !r.success);
    return (
      <div className="space-y-6">
        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            {progress.failed === 0 ? (
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            ) : progress.succeeded === 0 ? (
              <XCircle className="h-8 w-8 text-red-500" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{STRINGS.importComplete}</h3>
          <p className="text-sm text-gray-600 mt-1">
            <span className="text-green-600 font-medium">{progress.succeeded}</span>{' '}
            {STRINGS.successCount},{' '}
            <span className="text-red-600 font-medium">{progress.failed}</span>{' '}
            {STRINGS.failedCount}
          </p>
        </div>

        {failedResults.length > 0 && (
          <div className="max-h-48 overflow-y-auto border border-red-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-red-50 sticky top-0">
                  <th className="px-3 py-2 text-left font-medium text-red-700">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-red-700">Arsyeja</th>
                </tr>
              </thead>
              <tbody>
                {failedResults.map((r, i) => (
                  <tr key={i} className="border-t border-red-100">
                    <td className="px-3 py-2 text-gray-700">{r.email}</td>
                    <td className="px-3 py-2 text-red-600">{r.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            {STRINGS.btnClose}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">{STRINGS.importTitle}</h4>

        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={options.sendWelcomeEmail}
            onChange={(e) => setOptions({ ...options, sendWelcomeEmail: e.target.checked })}
            className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <Send className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">{STRINGS.sendEmail}</span>
        </label>

        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={options.autoGeneratePasswords}
            onChange={(e) =>
              setOptions({ ...options, autoGeneratePasswords: e.target.checked })
            }
            className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <KeyRound className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">{STRINGS.autoPassword}</span>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Shield className="h-4 w-4 text-gray-400" />
              {STRINGS.defaultRole}
            </label>
            <select
              value={options.defaultRole}
              onChange={(e) =>
                setOptions({ ...options, defaultRole: e.target.value as UserRole })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {Object.values(UserRole).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role] || role}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Users className="h-4 w-4 text-gray-400" />
              {STRINGS.defaultStatus}
            </label>
            <select
              value={options.defaultStatus}
              onChange={(e) =>
                setOptions({ ...options, defaultStatus: e.target.value as UserStatus })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value={UserStatus.ACTIVE}>{STATUS_LABELS.ACTIVE}</option>
              <option value={UserStatus.PENDING}>{STATUS_LABELS.PENDING}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-purple-900">
            {validUsers.length} {STRINGS.usersToImport}
          </span>
          {stats.totalWarnings > 0 && (
            <span className="text-xs text-yellow-700">
              {stats.totalWarnings} {STRINGS.warnings}
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          {STRINGS.btnBack}
        </button>
        <button
          onClick={startImport}
          disabled={validUsers.length === 0}
          className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {STRINGS.btnImport} ({validUsers.length})
        </button>
      </div>
    </div>
  );
}
